import { downloadBlob } from "./downloadBlob.js";
import { invariant } from "./invariant.js";

export type DownloadItem = {
	status: "cancelled" | "error" | "requested" | "downloading" | "downloaded" | "completed";
	bytesReceived?: number;
	error?: string;
};

export type DownloadParameters = {
	signal?: AbortSignal;
	fileName: string;
	onProgress?: (progress: DownloadItem) => void;
};

export async function downloadRequest(request: Request, { fileName, signal, onProgress }: DownloadParameters) {
	let bytesReceived = 0;

	onProgress?.({ status: "requested" });

	try {
		let response = await fetch(request);

		if (!response.ok || !response.body) {
			if (response.headers.get("content-type")?.includes("application/json")) {
				let data = await response.json();
				invariant(!data.error, data.error);
			}
			throw new Error(`${response.status} ${response.statusText}`);
		}

		try {
			const reader = response.body.getReader();
			const stream = new ReadableStream({
				start: (controller) => {
					function pump(): Promise<void> {
						return reader.read().then((read) => {
							if (read.done) {
								controller.close();
								return;
							}

							bytesReceived += read.value.length;
							onProgress?.({ bytesReceived, status: "downloading" });
							controller.enqueue(read.value);

							return pump();
						});
					}

					return pump();
				},
			});

			response = new Response(stream);
		} catch {}

		const blob = await response.blob();

		onProgress?.({ bytesReceived, status: "downloaded" });

		await downloadBlob(blob, fileName);

		onProgress?.({ bytesReceived, status: "completed" });
	} catch (err) {
		if (signal?.aborted) {
			onProgress?.({ status: "cancelled" });
		} else {
			onProgress?.({ error: (err as Error).message, status: "error" });
		}
	}
}
